import React, { useContext, useState, useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { Button, Heading, Text } from "@foris/avocado-suite";
import Modal from "@components/modal";
import { Context } from "../../context/GroupsManagerContext";
import { Types as EditionTypes } from "../../context/editions.reducer";
import { IParams } from "@models/IParams";
import {
  AdaptedGroup,
  AdaptedValidationErrorReason,
  ErrorsByGroupId,
  GroupEditionErrors,
} from "../../models";
import useIsElementVisible from "@common/hooks/useIsElementVisible";
import BottomErrors from "../BottomErrors/BottomErrors";
import BottomWarnings from "../BottomWarnings/BottomWarnings";
import savingAllowed from "../../utils/savingAllowed";
import splitGroupErrors from "../../utils/splitGroupErrors";
import allEditionsWithHardErrors from "../../utils/allEditionsWithHardErrors";
import FloatButton from "@common/components/float-button";
import BottomInfo from "../BottomInfo/BottomInfo";
import css from "./bottomDetail.module.scss";
import { useTranslation } from "react-i18next";

interface Props {
  onSave: () => Promise<void>;
  parentGroupHasError?: boolean;
  isLoading?: boolean;
  externalErrors?: GroupEditionErrors;
}

const BottomDetail: React.FC<Props> = ({
  onSave,
  parentGroupHasError,
  isLoading,
  externalErrors = {},
}) => {
  const { t } = useTranslation();
  const { state, dispatch } = useContext(Context);
  const { origin, scenario, workspace }: IParams = useParams();
  const [editedCapacitiesByCrn, setEditedCapacitiesByCrn] = useState<string[]>([]);
  const [displayModal, setDisplayModal] = useState(false);
  const [editedVisibilitiesByCrn, setEditedVisibilitiesByCrn] = useState<string[]>([]);
  const contextUrl = `${workspace}/${scenario}/${origin}`;
  const bottomActionsRef = useRef(null);

  const someEditionExists = Object.keys(state?.editions?.byGroupId ?? {}).length > 0;
  const isBottomActionsVisible = useIsElementVisible(bottomActionsRef, someEditionExists);

  const onModalClick = {
    primary: () => {
      setDisplayModal(false);
      dispatch({ type: EditionTypes.Clean });
      onSave();
    },
    secondary: () => setDisplayModal(false),
  };

  const campuses = useMemo(() => {
    const groupIds = Object.keys(state?.editions?.byGroupId || {});
    const campusesSet = new Set(
      groupIds.map(groupId => state?.data?.groupsById[groupId]?.campus?.code),
    );

    return Array.from(campusesSet).join(", ");
  }, [state?.editions?.byGroupId, state?.data?.groupsById]);

  /**
   * Split the `errorsByGroupId` by "hardness".
   */
  const [hardErrorsByGroupId, rawSoftErrorsByGroupId]: [ErrorsByGroupId, ErrorsByGroupId] =
    useMemo(() => {
      const errorsByGroupId = state?.editions?.errorsByGroupId ?? {};

      if (Object.keys(externalErrors) !== undefined) {
        Object.keys(externalErrors).forEach(key => {
          errorsByGroupId[key] = externalErrors[key];
        });
      }

      return splitGroupErrors(structuredClone(errorsByGroupId));
    }, [state?.editions?.errorsByGroupId, externalErrors]);

  const softErrorsByGroupId = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(rawSoftErrorsByGroupId).filter(
          ([groupId]) => !(groupId in hardErrorsByGroupId),
        ),
      ),
    [rawSoftErrorsByGroupId],
  );

  const someHardErrorExists =
    Object.keys(hardErrorsByGroupId).length > 0 || Boolean(parentGroupHasError);
  const someSoftErrorExists = Object.keys(softErrorsByGroupId).length > 0;
  const somePendingChangeExists = useMemo(() => {
    return (state?.data?.groups ?? []).some(
      (group: AdaptedGroup) =>
        !group?.isEditable?.allowed &&
        group?.isEditable?.reason === AdaptedValidationErrorReason.PendingChangeRequest,
    );
  }, [state?.data?.groups]);

  /**
   * After groups are edited, update the list of groups with edited
   * `capacities` and the list of groups with edited `visibilities`.
   */
  useEffect(() => {
    const { capacitiesById, visibilitiesById } = Object.entries(
      state?.editions?.byGroupId ?? {},
    ).reduce(
      ({ capacitiesById, visibilitiesById }, [groupId, edition]) => {
        const crn = state?.data?.groupsById[groupId]?.code;
        if (edition?.capacity !== undefined) capacitiesById.add(crn);
        if (edition?.visibleForEnrollment !== undefined) visibilitiesById.add(crn);
        return { capacitiesById, visibilitiesById };
      },
      { capacitiesById: new Set<string>(), visibilitiesById: new Set<string>() },
    );
    setEditedCapacitiesByCrn(Array.from(capacitiesById) as string[]);
    setEditedVisibilitiesByCrn(Array.from(visibilitiesById) as string[]);
  }, [state?.editions?.byGroupId]);

  return (
    <section className={css.bottom}>
      <Modal
        typeState="confirm"
        title={t("groups-manager.change-with-errors-modal.title")}
        show={displayModal}
        textButtonPrincipal={t("groups-manager.change-with-errors-modal.principal-button")}
        textButtonSecondary={t("groups-manager.change-with-errors-modal.secondary-button")}
        onClickPrincipal={onModalClick?.primary}
        onClickSecondary={onModalClick?.secondary}
        onClose={onModalClick?.secondary}
      >
        <Text>{t("groups-manager.change-with-errors-modal.description")}</Text>
      </Modal>

      {Object.keys(state?.editions?.byGroupId ?? {}).length > 0 && (
        <Heading type="h3" className={css.bottom_label}>
          {t("groups-manager.bottom-details.pending-changes")}
        </Heading>
      )}
      {editedCapacitiesByCrn?.length > 0 && (
        <Text className={css.bottom_text}>
          {t("groups-manager.bottom-details.groups-with-capacity-edited")}:{" "}
          {editedCapacitiesByCrn.join(" | ")}
        </Text>
      )}
      {editedVisibilitiesByCrn?.length > 0 && (
        <Text className={css.bottom_text}>
          {t("groups-manager.bottom-details.groups-with-visibility-edited")}:{" "}
          {editedVisibilitiesByCrn.join(" | ")}
        </Text>
      )}
      {Object.keys(state?.editions?.byGroupId ?? {}).length > 0 && (
        <Text className={css.bottom_text}>
          {t("groups-manager.bottom-details.campuses")}: {campuses}
        </Text>
      )}

      {somePendingChangeExists && <BottomInfo contextUrl={contextUrl} />}
      {(someHardErrorExists || someSoftErrorExists) && !isLoading && (
        <>
          {someHardErrorExists && (
            <BottomErrors
              errorsByGroupId={hardErrorsByGroupId}
              capacityError={{
                display: Boolean(parentGroupHasError),
                group: state?.data?.group,
              }}
              groupsById={state?.data?.groupsById}
            />
          )}
          {someSoftErrorExists && (
            <BottomWarnings
              errorsByGroupId={softErrorsByGroupId}
              groupsById={state?.data?.groupsById}
            />
          )}
        </>
      )}

      <div ref={bottomActionsRef} className={css.bottom_buttons}>
        {someEditionExists && (
          <>
            <Button
              disabled={!someEditionExists || isLoading}
              variant="ghost"
              onClick={() => dispatch({ type: EditionTypes.Clean })}
            >
              {t("groups-manager.bottom-details.cancel")}
            </Button>
            <Button
              disabled={
                parentGroupHasError ||
                allEditionsWithHardErrors(
                  state?.editions?.byGroupId,
                  state?.editions?.errorsByGroupId,
                ) ||
                !someEditionExists ||
                isLoading
              }
              onClick={() => {
                const someEditionIsntSavable = !savingAllowed(state?.editions?.errorsByGroupId);
                if (someEditionExists && someEditionIsntSavable) {
                  setDisplayModal(true);
                  return;
                }
                return onSave();
              }}
            >
              {t("groups-manager.bottom-details.save-changes")}
            </Button>
          </>
        )}
      </div>

      <FloatButton
        iconRight="chevrons-right"
        isVisible={someEditionExists && !isBottomActionsVisible && !isLoading}
        onClick={() => {
          bottomActionsRef.current.scrollIntoView({ behavior: "smooth" });
        }}
      >
        {t("groups-manager.bottom-details.validate-changes")}
      </FloatButton>
    </section>
  );
};

export default BottomDetail;
